import { Loan, LoanDetails } from './types';
import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { Document } from '../documents/types';
import { VersionType } from '../documents/enums';
import { _mockDocumentTypes } from '../documents/getDocumentTypes';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import { secureRandomInt, secureRandomBoolean } from 'src/utils/secureRandom';
import { buildIncludeQuery } from '../utils/queryBuilder';
import {
  _generateRandomDocumentName,
  _getPageIdsAndFields,
} from '../utils/documents.mock';
import { LoanAssociation } from './enums';
import { loanKeys } from 'src/queries/loans/useLoan';
import { z } from 'zod';
import { documentSchema } from './getLoanDocuments';

export interface GetLoanParams {
  id: string;
  includeAssociations?: LoanAssociation[];
}

export interface GetLoanResponse extends Loan, LoanDetails {}

const GetLoanResponseSchema = z
  .object({
    loan: z
      .object({
        docsToReview: z.number(),
        fieldsToReview: z.number(),
        checklistsToReview: z.number(),
        incomeClarityToReview: z.number(),
        documents: z.array(documentSchema).optional(),
      })
      .passthrough(),
  })
  .passthrough();

const getLoan: QueryFunction<
  GetLoanResponse,
  ReturnType<typeof loanKeys.byLoan>
> = async ({ queryKey }) => {
  if (isMockServices()) return await mock(_mockData);

  const [, params] = queryKey;

  const authTokenHttp = await getCompatibleAuthTokenHttp();

  const queryParams = buildIncludeQuery(params.includeAssociations);

  const loan = await authTokenHttp<{
    loan: GetLoanResponse;
  }>(`/loans/${params.id}${queryParams}`, {
    method: 'GET',
  });

  if (!loan.data) throw new Error('Failed to fetch loan');

  const validatedLoan = GetLoanResponseSchema.safeParse(loan.data);

  if (!validatedLoan.success) {
    const error = validatedLoan.error.format();
    console.error(`Get Loan by Id validation failed: ${error}`);
    throw new Error(`Failed to parse loan: ${params.id}.`);
  }

  // NOTE: Casting to unknown is a temporary fix until there is a Refactor for proper zod implementation.
  return validatedLoan.data.loan as unknown as GetLoanResponse;
};

export default getLoan;

export const _mockDocuments: Document[] = Array.from(
  { length: 50 },
  (_, id) => {
    return {
      id: crypto.randomUUID(),
      versionIdentifier:
        Object.values(VersionType)[
          Math.floor(id * Math.min(Object.values(VersionType).length, 2))
        ],
      isNew: id % 2 === 0,
      isViewed: id % 3 === 0,
      isVerified: id % 5 === 0,
      userApproved: id % 5 === 0,
      name: _generateRandomDocumentName(id),
      documentTypeId: _mockDocumentTypes[0 + Math.floor((id * 17) % 10)].id,
      confidence: ((id * 13) % 100) + 1,
      documentType: _mockDocumentTypes[0 + Math.floor((id * 17) % 10)],
      version: 0,
      ..._getPageIdsAndFields(id),
      documentTypeConfidence: ((id * 13) % 100) + 1,
    };
  },
);

// Mock checklist data for testing
const _mockChecklistResults = Array.from({ length: 15 }, (_, id) => ({
  loanChecklistRuleId: `checklist-${id}`,
  rules: Array.from({ length: secureRandomInt(1, 5) }, (_, ruleIndex) => ({
    id: `rule-${id}-${ruleIndex}`,
    name: `Rule ${ruleIndex + 1}`,
    result: secureRandomBoolean(0.3) ? 'fail' : 'pass', // 30% chance of failure
    evaluatedExpression: '5000 == 5000',
    errorMessage: secureRandomBoolean(0.1) ? 'Some error occurred' : '', // 10% chance of error
    trueConstraint: 'Pass message',
    falseConstraint: 'Fail message',
  })),
}));

export const _generateRandomBorrowerName = (id: number): string => {
  const prefixes = [
    'Cliff',
    'Faustina',
    'Judith',
    'Emmanuelle',
    'Geraldine',
    'Angelo',
  ];
  const suffixes = [
    'Mcconnell',
    'Orozco',
    'Velasquez',
    'Lucas',
    'Downs',
    'Lang',
    'Watts',
  ];

  const prefix = prefixes[Math.floor((id * 17) % prefixes.length)];
  const suffix = suffixes[Math.floor((id * 17) % suffixes.length)];

  return `${prefix} ${suffix}`;
};

export const _mockData: Loan & LoanDetails = {
  id: `LN-${crypto.randomUUID().substring(0, 5)}`,
  loanTypeId: `LT-${crypto.randomUUID().substring(0, 5)}`,
  loanNumber: `LN-${crypto.randomUUID().substring(0, 5)}`,
  borrowerId: _generateRandomBorrowerName(secureRandomInt(0, 99)),
  status: 'pending',
  metadata: {
    // Basic loan information
    loanType: 'HELOC',
    brokerName: 'Jhonny',
    countyCode: '600095',
    loanAmount: '1000000.00',
    loanNumber: 'MOS_TESTS',
    creditScore: '95',
    loanOfficer: 'Jane Smith',
    loanPurpose: 'Purchase',
    propertyType: 'Occupied by tenant',

    // Borrower information
    borrowerFirstName: 'JHONS',
    borrowerMiddleName: 'khey',
    borrowerLastName: 'Raj',

    // Employment information
    employerName: 'kens',
    employeeMiddleName: '',

    // Property information
    propertyAddressCity: 'Springfield',
    city: 'Springfield',
    state: 'IL',
    zipCode: '62704',
    apartmentUnit: 'Apt 4B',
    streetAddress: '123 Main St.',

    // Agent information
    sellerAgentEmail: 'agent@example.com',
  },
  // Document statistics
  documents: _mockDocuments,
  pageCount: 150,
  docsToReview: _mockDocuments.filter((doc) => doc.confidence < 90).length,
  incomeClarityToReview: 10,
  // Field statistics
  fieldsCount: 245,
  fieldsToReview: 25,

  // Checklist information - calculated dynamically
  checklistsCount: _mockChecklistResults.length,
  checklistsToReview: _mockChecklistResults.filter((checklist) =>
    checklist.rules.some(
      (rule) =>
        (typeof rule.result === 'boolean' && rule.result === false) ||
        (typeof rule.result === 'string' &&
          rule.result.toLowerCase() === 'false') ||
        (typeof rule.result === 'string' &&
          rule.result.toLowerCase() === 'fail') ||
        rule.errorMessage?.trim() !== '',
    ),
  ).length,

  // Financial projections
  projectedQualifyingPay: 120000.0,
  projectedDTI: 35,
  projectedLTV: 75,
  projectedCLTV: 80,

  // System fields
  isDeleted: false,
  createdAt: '2025-05-26T17:57:01.089Z',
  updatedAt: '2025-05-26T17:57:01.089Z',

  // Additional fields
  loanTransactions: [],

  generalProgress: 30,
};
